/* eslint-disable camelcase */
import React, { useEffect, useRef, useState } from "react";
import getConfig from "next/config";
import { ArticleCard } from "@themuse/design-system";
import { trackSnowplowImpressions, triggerSnowplowClickEvent } from "@themuse/snowplow-js-client";
import { useAppState } from "hooks/AppContext";
import {
    ApiArticle,
    ArticleListResult,
} from "interfaces/api/apiInterfaces";
import { fetchArticles } from "utils/api";
import styles from "./ArticleCardList.module.scss";
import {
    getClickEventProps,
    getImpressionProps,
    getLogoSrc,
    SnowplowPropsRequestProps,
    SnowplowRequestEventProps,
} from "./ArticleCardList.utils";

export type ArticleCartListProps = {
    size?: number;
    title?: string;
    trackingLocation?: string;
    viewMore?: React.ReactNode;
} & React.HTMLProps<HTMLDivElement>

export const ArticleCardList = (props: ArticleCartListProps) => {
    const {
        size,
        title,
        trackingLocation,
        viewMore,
        ...restProps
    } = props;
    const { publicRuntimeConfig: { museWebHost } } = getConfig();
    const { company: { legacy_id: companyId, short_name } } = useAppState();
    const [articles, setArticles] = useState<ApiArticle[]>([]);
    const [showMore, setShowMore] = useState<boolean>(false);
    const wrapper = useRef();

    const handleCardClick = (trackingData: SnowplowRequestEventProps) => {
        if (!trackingData.trackingLocation) {
            return;
        }
        const clickEventProps = getClickEventProps(trackingData);
        triggerSnowplowClickEvent(clickEventProps);
    };

    useEffect(() => {
        fetchArticles({
            company_short_name: short_name,
            size: String(size || 100),
        })
            .then((result: ArticleListResult) => {
                setArticles(result?.articles?.data ?? []);
                setShowMore(result?.pagination?.totalPages > 1);
            })
            .catch((error) => {
                console.error(error);
            });
    }, [short_name, size]);

    useEffect(() => {
        if (articles?.length) {
            trackSnowplowImpressions(wrapper.current || undefined);
        }
    }, [!!articles?.length]);

    return articles?.length ? (
        <section data-testid="article-card-list" {...restProps} ref={wrapper}>
            {title && (
                <h2
                    className={styles.heading}
                    data-testid="article-card-list__title"
                >
                    {title}
                </h2>
            )}
            <div className={styles.cardList}>
                {articles.map((article, i) => {
                    const { companySponsor: sponsor, legacyId, writer } = article;
                    const logoSrc = getLogoSrc({ sponsor, writer });
                    const trackingData: SnowplowPropsRequestProps = {
                        articleId: legacyId?.toString(),
                        companyId: companyId?.toString(),
                        tileOrder: i + 1,
                        trackingLocation,
                    };

                    return (
                        <ArticleCard
                            author={sponsor?.title ?? writer?.writerName}
                            category={article.primaryTopic?.title}
                            categoryUrl={
                                article.primaryTopic?.slug
                                && `${museWebHost}/advice/${article.primaryTopic?.slug}`
                            }
                            contentUrl={`${museWebHost}/${article.uri}`}
                            imgSrc={article.leadMediaPhoto?.url}
                            key={article.id}
                            logoSrc={logoSrc}
                            onCardClick={() => handleCardClick(trackingData)}
                            sponsorId={sponsor?.id}
                            title={article.title}
                            {...getImpressionProps(trackingData)}
                        />
                    );
                })}
            </div>
            {(showMore && viewMore) || null}
        </section>
    ) : null;
};
